import { money, dateFmt } from "./api";
import JsBarcode from "jsbarcode";

// Genera un SVG (string) de código de barras CODE128 listo para incrustar en el ticket
export function barcodeSvg(value) {
  try {
    const el = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    JsBarcode(el, value, { format: "CODE128", width: 2, height: 56, displayValue: true, fontSize: 14, margin: 0 });
    return el.outerHTML;
  } catch { return ""; }
}

// ---- Nota guardada (venta en espera) con código de barras para recuperarla en otra caja ----
export function notaGuardadaDoc(h) {
  return {
    title: "NOTA GUARDADA",
    meta: [
      { label: "Código", value: h.code },
      { label: "Fecha", value: dateFmt(h.created_at) },
      { label: "Atendió", value: h.cashier_name || "—" },
      ...(h.note ? [{ label: "Cliente", value: h.note }] : []),
    ],
    items: (h.items || []).map((it) => ({ qty: it.qty, name: it.name, price: it.price, variant: it.variant, amount: it.qty * it.price })),
    totals: [{ label: "TOTAL", value: money(h.total), big: true }],
    barcode: { value: h.code, svg: barcodeSvg(h.code) },
    note: "Escanea este código en cualquier caja para cargar la nota al carrito.",
    footer: "NOTA EN ESPERA · NO ES COMPROBANTE DE PAGO",
  };
}

// ---- Cotización (mismo formato que el ticket de venta) ----
export function cotizacionDoc(q) {
  const iva = Number(q.iva) || 0;
  const totals = [];
  if (iva > 0) { totals.push({ label: "Subtotal", value: money(q.subtotal) }); totals.push({ label: "IVA 16%", value: money(iva) }); }
  totals.push({ label: "TOTAL", value: money(q.total), big: true });
  return {
    title: "COTIZACIÓN",
    meta: [
      { label: "Folio", value: q.folio },
      { label: "Fecha", value: dateFmt(q.created_at) },
      ...(q.customer_name ? [{ label: "Cliente", value: q.customer_name }] : []),
      ...(q.customer_phone ? [{ label: "Tel", value: q.customer_phone }] : []),
      ...(q.valid_until ? [{ label: "Válida hasta", value: q.valid_until }] : []),
    ],
    items: (q.items || []).map((it) => ({ qty: it.qty, name: it.name, price: it.price, amount: it.qty * it.price })),
    totals,
    note: q.notes || null,
    footer: "COTIZACIÓN · NO ES FACTURA",
  };
}

// ---- Abono / pago a proveedor (mismo formato de ticket) ----
export function abonoProveedorDoc({ supplier, account, amount, concept, folio }) {
  return {
    title: "ABONO A PROVEEDOR",
    meta: [
      ...(folio ? [{ label: "Folio", value: String(folio) }] : []),
      { label: "Proveedor", value: supplier || "—" },
      ...(account ? [{ label: "Cuenta", value: account }] : []),
      { label: "Fecha", value: dateFmt(new Date().toISOString()) },
    ],
    items: [],
    totals: [{ label: "MONTO ABONADO", value: money(amount), big: true }],
    note: concept || null,
    footer: "COMPROBANTE DE ABONO",
  };
}

// ---- Venta (POS / historial) ----
export function ventaDoc(sale) {
  const iva = Number(sale.iva) || 0;
  const totals = [];
  if (iva > 0) { totals.push({ label: "Subtotal", value: money(sale.subtotal) }); totals.push({ label: "IVA", value: money(iva) }); }
  totals.push({ label: "TOTAL", value: money(sale.total), big: true });
  const payment = [];
  if (sale.payment_method) payment.push({ label: "Pago", value: (sale.payment_method || "").toUpperCase() });
  if (sale.payment_method === "mixto" && Array.isArray(sale.payments) && sale.payments.length) {
    const LBL = { efectivo: "Efectivo", tarjeta: "Tarjeta", transferencia: "Transferencia", credito: "Crédito" };
    sale.payments.forEach((p) => payment.push({ label: `  ${LBL[p.method] || p.method}`, value: money(p.amount) }));
  } else if (sale.cash_received > 0) payment.push({ label: "Recibido", value: money(sale.cash_received) });
  if (sale.change > 0) payment.push({ label: "Cambio", value: money(sale.change) });
  if (sale.card_balance_used > 0) payment.push({ label: "Pagado con monedero", value: `-${money(sale.card_balance_used)}` });
  if (sale.card_points_used > 0) payment.push({ label: `Canje ${sale.card_points_used} puntos`, value: `-${money(sale.card_points_value || 0)}` });
  if (sale.card) payment.push({ label: `Tarjeta ${sale.card.code}`, value: `${sale.card.points_earned > 0 ? `+${sale.card.points_earned} pts · ` : ""}saldo ${money(sale.card.balance_after)} · ${sale.card.points_after} pts` });
  (sale.loyalty_ops || []).forEach((o) => payment.push({ label: o.op === "activate" ? `Tarjeta ${o.code} activada` : `Recarga ${o.code}`, value: `saldo ${money(o.balance)}` }));
  return {
    title: "TICKET DE VENTA",
    isSale: true,
    banner: sale.is_whatsapp ? "VENTA POR WHATSAPP" : null,
    bannerName: sale.is_whatsapp ? (sale.whatsapp_name || sale.customer_name || "") : null,
    meta: [
      { label: "Folio", value: sale.folio },
      { label: "Fecha", value: dateFmt(sale.date) },
      { label: "Atendió", value: sale.cashier_name || "—" },
      ...(sale.customer_name ? [{ label: "Cliente", value: sale.customer_name }] : []),
      ...(sale.is_credit ? [{ label: "Venta a crédito", value: `Saldo ${money(sale.credit_part || sale.total)}` }] : []),
    ],
    items: (sale.items || []).map((it) => ({ qty: it.qty, name: it.name, price: it.price, variant: it.variant, lot: it.lot, amount: it.qty * it.price })),
    totals, payment,
    note: sale.comment || null,
    banners: [...(sale.is_credit ? ["** VENTA A CRÉDITO **"] : []), ...(sale.offline ? ["VENTA SIN CONEXIÓN · FOLIO PROVISIONAL"] : [])],
  };
}

// ---- Compra a proveedor ----
export function compraDoc(p) {
  return {
    title: "COMPROBANTE DE COMPRA",
    meta: [
      { label: "Folio", value: p.folio },
      { label: "Fecha", value: dateFmt(p.date) },
      { label: "Proveedor", value: p.supplier_name || "—" },
    ],
    items: (p.items || []).map((it) => ({ qty: it.qty, name: it.name, price: it.price != null ? it.price : it.cost, variant: it.variant, amount: it.qty * (it.price != null ? it.price : it.cost) })),
    totals: [{ label: "TOTAL", value: money(p.total), big: true }],
    payment: p.payment_mode ? [{ label: "Pago", value: (p.payment_mode || "").toUpperCase() }] : [],
    signature: "Firma de quien recibe",
  };
}

// ---- Devolución / cancelación ----
export function devolucionDoc(ret, company = {}) {
  const isCancel = ret.type === "cancel";
  return {
    title: isCancel ? "CANCELACIÓN DE VENTA" : "NOTA DE DEVOLUCIÓN",
    meta: [
      { label: "Folio", value: ret.folio },
      { label: "Venta", value: ret.sale_folio },
      { label: "Fecha", value: dateFmt(ret.date || new Date().toISOString()) },
      ...(company?.name ? [{ label: "Sucursal", value: company.name }] : []),
      { label: "Cajero", value: ret.cashier || "—" },
      ...(ret.customer_name ? [{ label: "Cliente", value: ret.customer_name }] : []),
    ],
    items: (ret.items || []).map((it) => ({ qty: it.qty, name: it.name, price: it.price, amount: it.qty * it.price, variant: it.variant || "" })),
    totals: [{ label: "TOTAL DEVUELTO", value: money(ret.total), big: true }],
    payment: [{ label: "Forma", value: ret.refund_mode === "favor" ? "SALDO A FAVOR" : "EFECTIVO" }],
    note: ret.reason ? `Motivo: ${ret.reason}` : null,
    signature: ret.received_by ? `Autorizó: ${ret.received_by}` : false,
    footer: isCancel ? "CANCELACIÓN" : "DEVOLUCIÓN",
  };
}

// ---- Corte de caja ----
export function corteDoc(p) {
  const b = p.breakdown || {};
  const blind = p.expected_cash == null;
  return {
    title: "CORTE DE CAJA",
    meta: [
      { label: "Folio", value: p.corte_folio },
      { label: "Cajero", value: p.user_name },
      { label: "Cierre", value: dateFmt(p.closed_at) },
      ...(blind ? [{ label: "Modo", value: "CORTE CIEGO" }] : []),
    ],
    rows: [
      ...(blind ? [] : [{ label: "Fondo inicial", value: money(p.opening_balance) }, { label: "Ventas efectivo", value: money((b.efectivo || 0) + (b.mixto_cash || 0)) }]),
      { label: "Ventas tarjeta", value: money(b.tarjeta || 0) },
      { label: "Ventas transfer.", value: money(b.transferencia || 0) },
      { label: "Ventas crédito", value: money(b.credito || 0) },
      { label: "Tickets", value: String(b.count ?? 0) },
      { sep: true },
      ...(blind
        ? [{ label: "Efectivo contado", value: money(p.counted_cash), bold: true }]
        : [{ label: "Efectivo esperado", value: money(p.expected_cash), bold: true }, { label: "Efectivo contado", value: money(p.counted_cash), bold: true }, { label: "Diferencia", value: money(p.difference), bold: true }]),
      { sep: true },
      { label: `A ${p.safe_name || "caja fuerte"}`, value: money(p.transferred_to_safe) },
      { label: "Queda en caja", value: money(p.left_in_drawer), bold: true },
    ],
    footer: "CORTE INTERNO",
  };
}

// ---- Traspaso caja → caja fuerte ----
export function traspasoDoc(p, copy = "") {
  return {
    title: copy ? `TRASPASO · COPIA ${copy}` : "TRASPASO DE EFECTIVO",
    meta: [
      { label: "Folio", value: p.folio },
      { label: "De", value: `Caja ${p.from_user || ""}`.trim() },
      { label: "A", value: p.safe_name },
      { label: "Fecha", value: dateFmt(p.date) },
    ],
    totals: [{ label: "MONTO TRASPASADO", value: money(p.amount), big: true }],
    signatures: copy ? ["Entrega: " + (p.from_user || "Cajero"), "Recibe: Encargado / Administrador"] : undefined,
    footer: copy ? `COMPROBANTE INTERNO · ${copy === "CAJERO" ? "Conserva este ticket" : "Entregar al encargado"}` : "COMPROBANTE INTERNO",
  };
}

// Dos tickets cortos: uno para el cajero y otro para el encargado/administrador
export function traspasoDocs(p) {
  return [traspasoDoc(p, "CAJERO"), traspasoDoc(p, "ENCARGADO")];
}

// ---- Transferencia genérica entre cuentas ----
export function transferDoc({ from_name, to_name, amount, concept }) {
  return {
    title: "TRANSFERENCIA",
    meta: [
      { label: "Fecha", value: dateFmt(new Date().toISOString()) },
      { label: "De", value: from_name },
      { label: "A", value: to_name },
      ...(concept ? [{ label: "Concepto", value: concept }] : []),
    ],
    totals: [{ label: "MONTO", value: money(amount), big: true }],
    footer: "COMPROBANTE INTERNO",
  };
}

// ---- Comprobante de pago genérico (gasto, interés, capital, inversionista) ----
export function pagoDoc(receipt) {
  return {
    title: receipt.subtitle || "COMPROBANTE DE PAGO",
    meta: [{ label: "Fecha", value: dateFmt(receipt.date || new Date().toISOString()) }],
    rows: (receipt.lines || []).map((l) => ({ label: l.label, value: l.value })),
    totals: [{ label: "IMPORTE", value: money(receipt.total), big: true }],
    signature: receipt.signature ? "Firma de conformidad" : false,
    footer: receipt.footer || "COMPROBANTE INTERNO",
  };
}

// ---- Recibo de nómina (uno por empleado) ----
export function nominaEmployeeDoc(line, payroll) {
  const rows = [{ label: "Sueldo", value: money(line.salary) }];
  if (line.increases > 0) rows.push({ label: "Aumentos", value: `+${money(line.increases)}` });
  if (line.decreases > 0) rows.push({ label: "Descuentos", value: `-${money(line.decreases)}` });
  return {
    title: "RECIBO DE NÓMINA",
    meta: [
      { label: "Folio", value: `#${payroll.folio}` },
      ...(payroll.week_start ? [{ label: "Semana", value: `${payroll.iso_week} · ${payroll.week_start} al ${payroll.week_end}` }] : []),
      { label: "Fecha", value: dateFmt(payroll.date) },
      { label: "Empleado", value: line.name },
      ...(line.position ? [{ label: "Puesto", value: line.position }] : []),
    ],
    rows,
    totals: [{ label: "NETO PAGADO", value: money(line.pay), big: true }],
    signature: "Firma del empleado",
    footer: "RECIBO DE PAGO",
  };
}

// ---- Abono a nota de crédito de cliente ----
export function abonoDoc({ folio, customer, amount, method, prev_balance, new_balance }) {
  const rows = [{ label: "Forma de pago", value: (method || "").toUpperCase() }];
  if (prev_balance != null) rows.push({ label: "Saldo anterior", value: money(prev_balance) });
  if (new_balance != null) rows.push({ label: "Saldo nuevo", value: money(new_balance) });
  return {
    title: "RECIBO DE ABONO",
    meta: [
      ...(folio ? [{ label: "Folio", value: folio }] : []),
      { label: "Fecha", value: dateFmt(new Date().toISOString()) },
      { label: "Cliente", value: customer },
    ],
    rows,
    totals: [{ label: "ABONADO", value: money(amount), big: true }],
    footer: "COMPROBANTE DE ABONO",
  };
}

// ---- Traspaso de mercancía entre sucursales ----
export function traspasoMercanciaDoc(t) {
  const ST = { pendiente: "PENDIENTE DE SURTIR", en_transito: "EN TRÁNSITO · PENDIENTE DE RECEPCIÓN", confirmado: "RECIBIDO · CONFIRMADO", rechazado: "RECHAZADO", cancelado: "CANCELADO" };
  return {
    title: "TRASPASO DE MERCANCÍA",
    meta: [
      { label: "Folio", value: `#${t.folio}` },
      { label: "Tipo", value: t.type === "envio" ? "Envío" : "Solicitud" },
      { label: "Origen", value: t.from_name },
      { label: "Destino", value: t.to_name },
      { label: "Fecha", value: dateFmt(t.created_at) },
      { label: "Registró", value: t.created_by || "—" },
      ...(t.supplied_by ? [{ label: "Surtió", value: `${t.supplied_by} · ${dateFmt(t.supplied_at)}` }] : []),
      ...(t.resolved_by ? [{ label: t.status === "confirmado" ? "Recibió" : "Cerró", value: `${t.resolved_by} · ${dateFmt(t.resolved_at)}` }] : []),
      ...(t.note ? [{ label: "Nota", value: t.note }] : []),
    ],
    items: (t.items || []).map((it) => ({ qty: it.qty, name: it.name + (it.variant ? ` (${it.variant})` : ""), price: null, amount: null })),
    rows: [{ label: "Total de artículos", value: String((t.items || []).reduce((a, it) => a + Number(it.qty || 0), 0)), bold: true }],
    banners: [ST[t.status] || String(t.status).toUpperCase()],
    signatures: ["Entrega (nombre y firma)", "Recibe (nombre y firma)"],
    footer: "COMPROBANTE DE TRASPASO · SIN VALOR FISCAL",
  };
}

// ---- Recibo de pago de licencia ----
export function reciboLicenciaDoc(r, lic) {
  const M = { tarjeta_mp: "Tarjeta · Mercado Pago", transferencia: "Transferencia bancaria" };
  return {
    title: "RECIBO DE PAGO · LICENCIA",
    meta: [
      { label: "Empresa", value: r.business_name || lic?.business_name || "" },
      { label: "Fecha de pago", value: dateFmt(r.verified_at || r.created_at) },
      { label: "Forma de pago", value: M[r.method] || r.method },
      { label: "Referencia", value: r.reference || "—" },
      { label: "Periodo", value: lic?.billing === "mensual" ? "Mensual" : "Anual" },
      { label: "Nueva vigencia", value: lic?.end_date ? `hasta ${new Date(lic.end_date).toLocaleDateString("es-MX")}` : "—" },
    ],
    totals: [{ label: "TOTAL PAGADO", value: money(r.amount), big: true }],
    footer: "GRACIAS POR TU PAGO · PROFESIONALPOS",
  };
}

// ---- Pedido Shopify (ticket de empaque) ----
export function shopifyOrderDoc(o, branchName = "") {
  const clean = (v) => (v && v !== "Default Title" ? v : "");
  return {
    title: `PEDIDO SHOPIFY ${o.name || ""}`,
    meta: [
      { label: "Fecha pedido", value: dateFmt(o.created_at) },
      ...(branchName ? [{ label: "Sucursal", value: branchName }] : []),
      { label: "Cliente", value: o.customer || "—" },
      ...(o.phone ? [{ label: "Teléfono", value: o.phone }] : []),
      ...(o.email ? [{ label: "Correo", value: o.email }] : []),
      ...(o.shipping ? [{ label: "Envío a", value: o.shipping }] : []),
      { label: "Pago", value: (o.financial_status || "—").toUpperCase() },
      ...(o.fulfilled_by ? [{ label: "Surtió", value: o.fulfilled_by }] : []),
    ],
    items: (o.items || []).map((it) => ({ qty: it.qty, name: it.title, variant: clean(it.variant_title), price: it.price, amount: it.qty * it.price })),
    totals: [
      { label: "Piezas", value: String((o.items || []).reduce((a, it) => a + Number(it.qty || 0), 0)) },
      { label: "TOTAL", value: money(o.total), big: true },
    ],
    note: o.note ? `Nota del cliente: ${o.note}` : "",
    footer: "TICKET DE EMPAQUE · PEDIDO EN LÍNEA",
  };
}
